import {
    ArrowDownLeft,
    ArrowUpRight,
    CheckCircle2,
    ChevronLeft,
    ChevronRight,
    Coins,
    Minus,
    Plus,
    RefreshCw,
    RotateCw,
    Wallet,
    Zap,
} from "lucide-react"
import moment from "moment"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { Link } from "react-router-dom"
import Layout from "../components/Layout"
import { PixPayment, PixPayload, PixQrSkeleton } from "../components/pix"
import {
    Badge,
    Button,
    Card,
    CardHeader,
    Input,
    StatTile,
    Table,
    TBody,
    Td,
    Th,
    THead,
    Tr,
} from "../components/ui"
import { LedgerEntry, useInvalidateWallet, useWallet } from "../hooks/useWallet"
import { cn } from "../utils/cn"
import api from "../utils/axios"

// ---- contratos do backend (data-credits.ts) ---------------------------------
interface BuyResponse {
    purchase_id: number
    quantity: number
    valor: number
    status: "PENDING" | "CONCLUIDA"
    pix?: PixPayload
    balance?: number
    bypass?: boolean
}

const PRESETS = [5, 10, 25, 50, 100]
const POLL_MS = 5000
const LEDGER_PAGE_SIZE = 10

const brl = (v: number) =>
    v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })

const reasonMeta: Record<
    string,
    { label: string; icon: React.ReactNode }
> = {
    PURCHASE: {
        label: "Compra de créditos",
        icon: <ArrowDownLeft size={14} className="text-success-text" />,
    },
    SPEND: {
        label: "Ativação de acesso",
        icon: <Zap size={14} className="text-primary" />,
    },
    ADJUSTMENT: {
        label: "Ajuste manual",
        icon: <RotateCw size={14} className="text-muted" />,
    },
    REFUND: {
        label: "Estorno",
        icon: <ArrowUpRight size={14} className="text-warning-text" />,
    },
}

/**
 * Máquina de estados da compra:
 * idle → generating (servidor gerando a cobrança; QR em esqueleto)
 *      → pending (QR na tela + polling) → paid | expired | error
 */
type Purchase =
    | { step: "idle" }
    | { step: "generating"; quantity: number }
    | { step: "pending"; data: BuyResponse; pix: PixPayload }
    | { step: "expired"; data: BuyResponse }
    | { step: "paid"; quantity: number; balance: number }
    | { step: "error"; message: string; quantity: number }

/** Carteira de créditos do provedor: saldo, compra via PIX e extrato. */
const CreditsPage: React.FC = () => {
    const { data: wallet, isLoading, isFetching, error, refetch } = useWallet()
    const invalidateWallet = useInvalidateWallet()

    const [quantity, setQuantity] = useState(10)
    const [purchase, setPurchase] = useState<Purchase>({ step: "idle" })

    // Extrato paginado no cliente (o backend devolve até 50 lançamentos).
    const [ledgerPage, setLedgerPage] = useState(1)
    const ledger = wallet?.ledger ?? []
    const ledgerPages = Math.max(1, Math.ceil(ledger.length / LEDGER_PAGE_SIZE))
    const safeLedgerPage = Math.min(ledgerPage, ledgerPages)
    const ledgerRows = ledger.slice(
        (safeLedgerPage - 1) * LEDGER_PAGE_SIZE,
        safeLedgerPage * LEDGER_PAGE_SIZE
    )
    const ledgerFirst =
        ledger.length === 0 ? 0 : (safeLedgerPage - 1) * LEDGER_PAGE_SIZE + 1
    const ledgerLast = Math.min(safeLedgerPage * LEDGER_PAGE_SIZE, ledger.length)
    const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null)

    const unitPrice = wallet?.unit_price
    const balance = wallet?.balance ?? 0
    const total = unitPrice !== undefined ? unitPrice * quantity : undefined

    const stats = useMemo(() => {
        const ledger = wallet?.ledger ?? []
        return {
            bought: ledger
                .filter((e) => e.reason === "PURCHASE")
                .reduce((a, e) => a + e.delta, 0),
            spent: ledger
                .filter((e) => e.reason === "SPEND")
                .reduce((a, e) => a + Math.abs(e.delta), 0),
        }
    }, [wallet])

    // ---- polling de confirmação (fallback do webhook) ------------------------
    const stopPolling = () => {
        if (pollTimer.current) clearInterval(pollTimer.current)
        pollTimer.current = null
    }
    useEffect(() => stopPolling, [])

    const startPolling = (data: BuyResponse) => {
        stopPolling()
        pollTimer.current = setInterval(async () => {
            try {
                const res = await api.post<{ status: string; balance: number }>(
                    "/credits/purchase-status",
                    { purchase_id: data.purchase_id }
                )
                if (res.data.status === "CONCLUIDA") {
                    stopPolling()
                    setPurchase({
                        step: "paid",
                        quantity: data.quantity,
                        balance: res.data.balance,
                    })
                    invalidateWallet()
                }
            } catch {
                /* mantém tentando até expirar/cancelar */
            }
        }, POLL_MS)
    }

    // ---- comprar -------------------------------------------------------------
    const buy = async (qty = quantity) => {
        if (!Number.isInteger(qty) || qty < 1) return
        setPurchase({ step: "generating", quantity: qty })
        try {
            const res = await api.post<BuyResponse>("/credits/buy", {
                quantity: qty,
            })
            if (res.data.status === "CONCLUIDA" || !res.data.pix) {
                // Bypass/staging: creditado na hora.
                setPurchase({
                    step: "paid",
                    quantity: res.data.quantity,
                    balance: res.data.balance ?? balance + res.data.quantity,
                })
                invalidateWallet()
            } else {
                setPurchase({ step: "pending", data: res.data, pix: res.data.pix })
                startPolling(res.data)
            }
        } catch (err: any) {
            setPurchase({
                step: "error",
                quantity: qty,
                message:
                    err.response?.data?.error ||
                    "Falha ao gerar a cobrança PIX. Tente novamente.",
            })
        }
    }

    const cancel = () => {
        stopPolling()
        setPurchase({ step: "idle" })
    }

    const setQty = (v: number) => setQuantity(Math.min(1000, Math.max(1, v)))

    // ---- render --------------------------------------------------------------
    const renderPurchase = () => {
        switch (purchase.step) {
            case "generating":
                return (
                    <div className="flex flex-col items-center gap-2 py-2">
                        <PixQrSkeleton
                            label={`Gerando PIX de ${purchase.quantity} crédito${
                                purchase.quantity > 1 ? "s" : ""
                            }${total !== undefined ? ` · ${brl(
                                (unitPrice ?? 0) * purchase.quantity
                            )}` : ""}...`}
                        />
                    </div>
                )
            case "pending":
                return (
                    <div className="flex flex-col items-center gap-3 py-2">
                        <p className="text-center text-sm text-muted">
                            Pague{" "}
                            <strong className="text-text">
                                {brl(purchase.data.valor)}
                            </strong>{" "}
                            para receber{" "}
                            <strong className="text-text">
                                {purchase.data.quantity} crédito
                                {purchase.data.quantity > 1 ? "s" : ""}
                            </strong>
                            . O saldo é creditado assim que o banco confirmar.
                        </p>
                        <PixPayment
                            pix={purchase.pix}
                            onExpire={() => {
                                stopPolling()
                                setPurchase({
                                    step: "expired",
                                    data: purchase.data,
                                })
                            }}
                        />
                        <Button variant="ghost" size="sm" onClick={cancel}>
                            Cancelar
                        </Button>
                    </div>
                )
            case "expired":
                return (
                    <div className="flex flex-col items-center gap-3 py-4 text-center">
                        <p className="text-sm text-muted">
                            Este PIX expirou sem pagamento. Gere um novo para
                            continuar.
                        </p>
                        <div className="flex gap-2">
                            <Button
                                onClick={() => buy(purchase.data.quantity)}
                                leftIcon={<RefreshCw size={15} />}
                            >
                                Gerar novo PIX
                            </Button>
                            <Button variant="ghost" onClick={cancel}>
                                Voltar
                            </Button>
                        </div>
                    </div>
                )
            case "paid":
                return (
                    <div className="flex flex-col items-center gap-2 py-4 text-center">
                        <CheckCircle2 className="h-10 w-10 text-success-text" />
                        <p className="text-base font-semibold text-success-text">
                            {purchase.quantity} crédito
                            {purchase.quantity > 1 ? "s" : ""} adicionado
                            {purchase.quantity > 1 ? "s" : ""}
                        </p>
                        <p className="text-sm text-muted">
                            Saldo atual:{" "}
                            <strong className="text-text">
                                {purchase.balance}
                            </strong>
                        </p>
                        <div className="mt-2 flex gap-2">
                            <Button onClick={cancel}>Comprar mais</Button>
                            <Link to="/devices">
                                <Button variant="outline">
                                    Ir para dispositivos
                                </Button>
                            </Link>
                        </div>
                    </div>
                )
            case "error":
            case "idle":
            default:
                return (
                    <div className="flex flex-col gap-4">
                        <div>
                            <span className="mb-2 block text-xs font-medium text-muted">
                                Quantidade
                            </span>
                            <div className="flex flex-wrap items-center gap-2">
                                {PRESETS.map((n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        onClick={() => setQty(n)}
                                        className={cn(
                                            "rounded-lg border px-3 py-1.5 text-sm font-medium tabular-nums transition-colors",
                                            quantity === n
                                                ? "border-primary bg-primary/15 text-primary"
                                                : "border-border text-muted hover:border-border-strong hover:text-text"
                                        )}
                                    >
                                        {n}
                                    </button>
                                ))}
                                <div className="ml-auto flex items-center gap-1">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="icon"
                                        className="h-9 w-9 p-0"
                                        onClick={() => setQty(quantity - 1)}
                                        aria-label="Diminuir"
                                    >
                                        <Minus size={14} />
                                    </Button>
                                    <Input
                                        type="number"
                                        min={1}
                                        max={1000}
                                        value={quantity}
                                        onChange={(e) =>
                                            setQty(
                                                Math.floor(
                                                    Number(e.target.value)
                                                ) || 1
                                            )
                                        }
                                        className="h-9 w-20 text-center tabular-nums"
                                        aria-label="Quantidade de créditos"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="icon"
                                        className="h-9 w-9 p-0"
                                        onClick={() => setQty(quantity + 1)}
                                        aria-label="Aumentar"
                                    >
                                        <Plus size={14} />
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col gap-3 rounded-xl border border-border bg-elevated/50 p-4 sm:flex-row sm:items-center sm:justify-between">
                            <div className="text-sm text-muted">
                                {unitPrice !== undefined ? (
                                    <>
                                        {quantity} × {brl(unitPrice)} ={" "}
                                        <span className="text-lg font-bold text-text">
                                            {brl(total ?? 0)}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        {quantity} crédito
                                        {quantity > 1 ? "s" : ""}
                                    </>
                                )}
                            </div>
                            <Button
                                type="button"
                                onClick={() => buy()}
                                leftIcon={<Wallet size={16} />}
                                className="min-w-[200px]"
                            >
                                {total !== undefined
                                    ? `Gerar PIX de ${brl(total)}`
                                    : `Comprar ${quantity} crédito${
                                          quantity > 1 ? "s" : ""
                                      }`}
                            </Button>
                        </div>

                        {purchase.step === "error" && (
                            <div className="rounded-lg border border-danger/40 bg-danger-soft p-3 text-sm text-danger-text">
                                {purchase.message}
                            </div>
                        )}
                    </div>
                )
        }
    }

    return (
        <Layout title="Carteira">
            <div className="flex flex-col gap-6">
                {/* Resumo */}
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                    <StatTile
                        label="Saldo"
                        value={
                            isLoading ? (
                                <span className="inline-block h-7 w-12 animate-pulse rounded bg-elevated" />
                            ) : (
                                balance
                            )
                        }
                        accent={balance > 0 ? "success" : "warning"}
                    />
                    <StatTile
                        label="Preço por crédito"
                        value={unitPrice !== undefined ? brl(unitPrice) : "n/d"}
                        accent="primary"
                    />
                    <StatTile
                        label="Créditos comprados"
                        value={stats.bought}
                        accent="neutral"
                    />
                    <StatTile
                        label="Ativações"
                        value={stats.spent}
                        accent="neutral"
                    />
                </div>

                {error && (
                    <div className="rounded-lg border border-danger/40 bg-danger-soft p-4 text-sm text-danger-text">
                        {error.message}
                    </div>
                )}

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
                    {/* Comprar */}
                    <Card className="self-start lg:col-span-2">
                        <CardHeader
                            title={
                                <span className="inline-flex items-center gap-2">
                                    <Coins className="h-5 w-5 text-primary" />
                                    Comprar créditos
                                </span>
                            }
                            description="Um PIX credita a quantidade escolhida. Cada ativação ou renovação de acesso consome 1 crédito."
                        />
                        <div className="mt-5">{renderPurchase()}</div>
                    </Card>

                    {/* Extrato */}
                    <Card flush className="self-start lg:col-span-3">
                        <div className="border-b border-border p-5 sm:p-6">
                            <CardHeader
                                title="Extrato"
                                description="Últimos lançamentos da carteira."
                                actions={
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => refetch()}
                                        loading={isFetching}
                                        leftIcon={
                                            isFetching ? undefined : (
                                                <RefreshCw size={14} />
                                            )
                                        }
                                    >
                                        Atualizar
                                    </Button>
                                }
                            />
                        </div>
                        {isLoading ? (
                            <div className="space-y-2 p-5">
                                {[0, 1, 2].map((i) => (
                                    <div
                                        key={i}
                                        className="h-9 animate-pulse rounded bg-elevated"
                                    />
                                ))}
                            </div>
                        ) : !wallet || wallet.ledger.length === 0 ? (
                            <div className="px-6 py-14 text-center text-sm text-muted">
                                Nenhum lançamento ainda. Compre créditos para
                                começar.
                            </div>
                        ) : (
                            <Table className="table-fixed">
                                <colgroup>
                                    <col className="w-[150px]" />
                                    <col />
                                    <col className="w-[80px]" />
                                    <col className="w-[90px]" />
                                </colgroup>
                                <THead>
                                    <tr>
                                        <Th>Data</Th>
                                        <Th>Lançamento</Th>
                                        <Th className="text-right">Δ</Th>
                                        <Th className="text-right">Saldo</Th>
                                    </tr>
                                </THead>
                                <TBody>
                                    {ledgerRows.map((e: LedgerEntry) => {
                                        const meta = reasonMeta[e.reason]
                                        return (
                                            <Tr key={e.id}>
                                                <Td className="whitespace-nowrap py-2 text-xs text-muted">
                                                    {moment(e.createdat).format(
                                                        "DD/MM/YYYY HH:mm"
                                                    )}
                                                </Td>
                                                <Td className="py-2">
                                                    <span className="inline-flex items-center gap-2">
                                                        {meta?.icon}
                                                        <span>
                                                            {meta?.label ??
                                                                e.reason}
                                                        </span>
                                                        {e.ref_id != null && (
                                                            <span className="text-xs text-subtle">
                                                                #{e.ref_id}
                                                            </span>
                                                        )}
                                                    </span>
                                                </Td>
                                                <Td className="py-2 text-right">
                                                    <Badge
                                                        variant={
                                                            e.delta >= 0
                                                                ? "success"
                                                                : "neutral"
                                                        }
                                                    >
                                                        {e.delta >= 0
                                                            ? `+${e.delta}`
                                                            : e.delta}
                                                    </Badge>
                                                </Td>
                                                <Td className="py-2 text-right text-sm tabular-nums text-muted">
                                                    {e.balance_after}
                                                </Td>
                                            </Tr>
                                        )
                                    })}
                                </TBody>
                            </Table>
                        )}
                        {ledger.length > 0 && (
                            <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-2 text-xs text-muted">
                                <span>
                                    Mostrando {ledgerFirst}–{ledgerLast} de{" "}
                                    {ledger.length}
                                </span>
                                <div className="flex items-center gap-1">
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-8 w-8 p-0"
                                        aria-label="Página anterior"
                                        disabled={safeLedgerPage <= 1}
                                        onClick={() =>
                                            setLedgerPage((p) => p - 1)
                                        }
                                    >
                                        <ChevronLeft size={16} />
                                    </Button>
                                    <span className="min-w-[56px] text-center tabular-nums">
                                        {safeLedgerPage} / {ledgerPages}
                                    </span>
                                    <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-8 w-8 p-0"
                                        aria-label="Próxima página"
                                        disabled={safeLedgerPage >= ledgerPages}
                                        onClick={() =>
                                            setLedgerPage((p) => p + 1)
                                        }
                                    >
                                        <ChevronRight size={16} />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Card>
                </div>
            </div>
        </Layout>
    )
}

export default CreditsPage
